import React, { useEffect, useMemo, useState } from 'react'
import useWindowSize from '../../hooks/useWindowSize'
import Dropdown from '../Dropdown'
import getHeaderTitles from './getHeaderTitles'
import * as Styled from './Table.styles'

const Table = ({
  children,
  leftColumnAlignment = false,
  mobileComparison = false,
}) => {
  const [isError, setIsError] = useState(false)
  const [tableData, setTableData] = useState(children)
  const [leftColumnError, setLeftColumnError] = useState(false)
  const [rightColumnError, setRightColumnError] = useState(false)
  const screenSize = useWindowSize()
  const isMobileView = screenSize.width < 940

  const headerCells = useMemo(() => {
    const table = children.find((child) => child?.type === 'table')
    return table?.props?.children?.[0]?.props?.children
  }, [children])

  const showMobileComparison =
    isMobileView && mobileComparison && !isError && headerCells?.length > 1

  const columnOptions = useMemo(
    () =>
      headerCells?.map((cell) => {
        const headerChildren =
          cell?.props?.children?.[1]?.props?.children ||
          cell.props.children?.[0].props?.children

        const headerTitles = getHeaderTitles(headerChildren)

        return {
          value: cell?.key,
          label: headerTitles?.find((text) => typeof text === 'string'),
        }
      }),
    [headerCells] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const [leftColumnFilter, setLeftColumnFilter] = useState(
    () => columnOptions?.[0]
  )
  const [rightColumnFilter, setRightColumnFilter] = useState(
    () => columnOptions?.[1]
  )

  const filterTableRows = (leftFilter, rightFilter) => {
    const data = [children.find((x) => x.type === 'table')]

    try {
      return data?.map((child) => {
        const rows = child.props?.children

        const filteredRows = rows.map((row) => {
          const leftCell = row.props?.children.find(
            (cell) => cell.key === leftFilter?.value
          )
          const rightCell = row.props?.children.find(
            (cell) => cell.key === rightFilter?.value
          )
          return {
            ...row,
            props: {
              ...row.props,
              children: [leftCell, rightCell].filter(Boolean),
            },
          }
        })

        return { ...child, props: { ...child.props, children: filteredRows } }
      })
    } catch (error) {
      console.error('Error filtering rows:', error) // eslint-disable-line no-console
      return data
    }
  }

  useEffect(() => {
    if (!showMobileComparison || typeof window === 'undefined') {
      return
    }

    if (leftColumnFilter && rightColumnFilter) {
      try {
        const newTableData = filterTableRows(
          leftColumnFilter,
          rightColumnFilter
        )

        setTableData(newTableData)
      } catch (error) {
        console.error('Error in filtering rows:', error) // eslint-disable-line no-console
        setTableData(children)
        setIsError(true)
      }
    }
  }, [leftColumnFilter, rightColumnFilter, children]) // eslint-disable-line react-hooks/exhaustive-deps

  const onLeftColumnSelect = (option) => {
    if (option.value === rightColumnFilter.value) {
      setLeftColumnError(true)
    } else {
      setLeftColumnError(false)
      setRightColumnError(false)
      setLeftColumnFilter(option)
    }
  }

  const onRightColumnSelect = (option) => {
    if (option.value === leftColumnFilter.value) {
      setRightColumnError(true)
    } else {
      setRightColumnError(false)
      setLeftColumnError(false)
      setRightColumnFilter(option)
    }
  }

  return (
    <Styled.TableWrapper
      className="leshen-table-wrapper"
      leftColumnAlignment={leftColumnAlignment}
    >
      {showMobileComparison ? (
        <Styled.Container>
          <Styled.Dropdowns>
            <Styled.DropdownWrapper>
              <Styled.ErrorWrapper showError={leftColumnError}>
                Select a new option
              </Styled.ErrorWrapper>
              <Dropdown
                placeholderText={leftColumnFilter?.label}
                options={columnOptions}
                onChange={onLeftColumnSelect}
              />
            </Styled.DropdownWrapper>
            <Styled.DropdownWrapper>
              <Styled.ErrorWrapper showError={rightColumnError}>
                Select a new option
              </Styled.ErrorWrapper>
              <Dropdown
                placeholderText={rightColumnFilter?.label}
                options={columnOptions}
                onChange={onRightColumnSelect}
              />
            </Styled.DropdownWrapper>
          </Styled.Dropdowns>
          {tableData[0]}
        </Styled.Container>
      ) : (
        children
      )}
    </Styled.TableWrapper>
  )
}

const TableWrapper = (props) => {
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [setMounted])

  return mounted && <Table {...props} />
}

export default TableWrapper
